import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Copy, Edit, Trash2, Eye, EyeOff, Globe, Table, Grid, ChevronUp, ChevronDown, Lock, X } from 'lucide-react';
import { Password, Tag as TagType } from '../../types';
import { passwordManagerAPI } from '../../services/api';
import { useSearch } from '../../contexts/SearchContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import loggingService from '../../services/loggingService';

const Passwords: React.FC = () => {
	const { globalSearchTerm } = useSearch();
	const { isDark } = useDarkMode();
	const [passwords, setPasswords] = useState<Password[]>([]);
	const [tags, setTags] = useState<TagType[]>([]);
	const [showModal, setShowModal] = useState(false);
	const [editingPassword, setEditingPassword] = useState<Password | null>(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [showPasswords, setShowPasswords] = useState<{ [key: number]: boolean }>({});
	const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
	const [sortField, setSortField] = useState<string | null>(null);
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
	const hasLoggedPageView = useRef(false);
	const [formData, setFormData] = useState({
		title: '',
		username: '',
		email: '',
		password: '',
		website: '',
		description: '',
		tag_ids: [] as number[]
	});

	useEffect(() => {
		loadData();
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/password-manager/passwords', 'Password Manager', 'Passwords');
			hasLoggedPageView.current = true;
		}
	}, []);

	const loadData = async () => {
		try {
			const [passwordsResponse, tagsResponse] = await Promise.all([
				passwordManagerAPI.passwords.getAll(),
				passwordManagerAPI.tags.getAll()
			]);
			setPasswords(passwordsResponse.data.data);
			setTags(tagsResponse.data.data);
		} catch (error) {
			console.error('Error loading data:', error);
		} finally {
			setLoading(false);
		}
	};

	const filteredPasswords = passwords.filter(password => {
		const matchesSearch = globalSearchTerm === '' ||
			password.title.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			password.description?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			password.username?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			password.email?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
			password.website?.toLowerCase().includes(globalSearchTerm.toLowerCase());
		return matchesSearch;
	});

	const sortedPasswords = [...filteredPasswords].sort((a, b) => {
		if (!sortField) return 0;
		
		let aValue: any = a[sortField as keyof Password];
		let bValue: any = b[sortField as keyof Password];
		
		if (sortField === 'created_at') {
			aValue = new Date(aValue).getTime();
			bValue = new Date(bValue).getTime();
		} else if (typeof aValue === 'string') {
			aValue = aValue.toLowerCase();
			bValue = bValue?.toLowerCase() || '';
		} else if (typeof aValue === 'boolean') {
			aValue = aValue ? 1 : 0;
			bValue = bValue ? 1 : 0;
		}
		
		if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
		if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
		return 0;
	});

	const handleSort = (field: string) => {
		if (sortField === field) {
			if (sortDirection === 'asc') {
				setSortDirection('desc');
			} else {
				// 3. tıklama: sıralamayı kaldır
				setSortField(null);
				setSortDirection('asc');
			}
		} else {
			setSortField(field);
			setSortDirection('asc');
		}
	};

	const SortIcon = ({ field }: { field: string }) => {
		if (sortField !== field) return null;
		return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 inline ml-1" /> : <ChevronDown className="w-4 h-4 inline ml-1" />;
	};

	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
		} catch (error) {
			console.error('Failed to copy:', error);
		}
	};

	const togglePasswordVisibility = (passwordId: number) => {
		setShowPasswords(prev => ({ ...prev, [passwordId]: !prev[passwordId] }));
	};

	const handleDelete = async (id: number) => {
		if (window.confirm('Are you sure you want to delete this Password?')) {
			try {
				await passwordManagerAPI.passwords.delete(id);
				setPasswords(passwords.filter(password => password.id !== id));
			} catch (error) {
				console.error('Error deleting password:', error);
			}
		}
	};

	const generatePassword = (length: number = 16, includeSymbols: boolean = true): string => {
		const lowercase = 'abcdefghijklmnopqrstuvwxyz';
		const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
		const numbers = '0123456789';
		const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';

		let charset = lowercase + uppercase + numbers;
		if (includeSymbols) {
			charset += symbols;
		}

		let password = '';
		password += lowercase[Math.floor(Math.random() * lowercase.length)];
		password += uppercase[Math.floor(Math.random() * uppercase.length)];
		password += numbers[Math.floor(Math.random() * numbers.length)];
		if (includeSymbols) {
			password += symbols[Math.floor(Math.random() * symbols.length)];
		}

		for (let i = password.length; i < length; i++) {
			password += charset[Math.floor(Math.random() * charset.length)];
		}

		return password.split('').sort(() => Math.random() - 0.5).join('');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitting(true);
		try {
			const response = await passwordManagerAPI.passwords.create(formData);
			const newPassword = response.data.data;
			setPasswords([...passwords, newPassword]);
			setShowModal(false);
			setFormData({ title: '', username: '', email: '', password: '', website: '', description: '', tag_ids: [] });
		} catch (error) {
			console.error('Error creating password:', error);
		} finally {
			setSubmitting(false);
		}
	};

	const handleEditSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!editingPassword) return;
		setSubmitting(true);
		try {
			const response = await passwordManagerAPI.passwords.update(editingPassword.id, formData);
			const updatedPassword = response.data.data;
			setPasswords(passwords.map(password => password.id === editingPassword.id ? updatedPassword : password));
			setShowModal(false);
			setEditingPassword(null);
			setFormData({ title: '', username: '', email: '', password: '', website: '', description: '', tag_ids: [] });
		} catch (error) {
			console.error('Error updating password:', error);
		} finally {
			setSubmitting(false);
		}
	};

	const openEditModal = (password: Password) => {
		setEditingPassword(password);
		setFormData({
			title: password.title,
			username: password.username || '',
			email: password.email || '',
			password: password.password,
			website: password.website || '',
			description: password.description || '',
			tag_ids: password.tag_names ? password.tag_names.split(',').map(name => tags.find(t => t.name === name)?.id || 0).filter(id => id > 0) : []
		});
		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
		setEditingPassword(null);
		setFormData({ title: '', username: '', email: '', password: '', website: '', description: '', tag_ids: [] });
	};

	const handleGeneratePassword = () => {
		const generatedPassword = generatePassword(16, true);
		setFormData({ ...formData, password: generatedPassword });
	};

	if (loading) {
		return (
			<div className="flex flex-col items-center justify-center h-64 font-mono">
				<div className="w-10 h-10 border-2 border-t-transparent rounded-none animate-spin border-emerald-500 mb-4" />
				<span className="text-[10px] uppercase tracking-[0.2em] text-emerald-500/70 animate-pulse">
					[INITIATING_SEC_PROTOCOL...]
				</span>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
				<div>
					<h1 className={`text-xl font-mono font-bold uppercase tracking-[0.2em] ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
						[PASS.SECURE_VAULT]
					</h1>
					<p className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
						{filteredPasswords.length} {filteredPasswords.length === 1 ? 'ENTRY_PROTECTED' : 'ENTRIES_PROTECTED'}
					</p>
				</div>
				<div className="flex items-center space-x-3">
					<div className="flex items-center space-x-1 border rounded-none p-0.5 overflow-hidden" style={{ borderColor: isDark ? '#222' : '#e5e7eb' }}>
						<button
							onClick={() => setViewMode('table')}
							className={`p-1.5 ${viewMode === 'table' ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'text-neutral-600 hover:text-neutral-400' : 'text-neutral-400 hover:text-neutral-600')} transition-all`}
							title="Table View"
						>
							<Table className="w-4 h-4" />
						</button>
						<button
							onClick={() => setViewMode('grid')}
							className={`p-1.5 ${viewMode === 'grid' ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'text-neutral-600 hover:text-neutral-400' : 'text-neutral-400 hover:text-neutral-600')} transition-all`}
							title="Grid View"
						>
							<Grid className="w-4 h-4" />
						</button>
					</div>
					<button
						onClick={() => setShowModal(true)}
						className={`flex items-center px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all rounded-none border ${isDark ? 'border-emerald-500/50 bg-[#111] text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500' : 'border-emerald-600/50 bg-white text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600'}`}
					>
						<Plus className="w-3.5 h-3.5 mr-2" />
						<span>NEW_ENTRY</span>
					</button>
				</div>
			</div>
			{filteredPasswords.length === 0 ? (
				<div className={`flex flex-col items-center justify-center py-12 rounded-none border ${isDark ? 'border-neutral-800 bg-[#0a0a0a]' : 'border-neutral-200 bg-white'}`}>
					<Lock className={`w-12 h-12 mb-4 ${isDark ? 'text-emerald-500/50' : 'text-emerald-700/50'}`} />
					<p className={`text-sm font-mono uppercase tracking-widest ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
						[VAULT_EMPTY]
					</p>
					<p className={`text-[10px] font-mono uppercase tracking-widest mt-1 ${isDark ? 'text-neutral-600' : 'text-neutral-300'}`}>
						[ADD_FIRST_ENTRY_TO_BEGIN]
					</p>
					<button
						onClick={() => setShowModal(true)}
						className={`mt-6 inline-flex items-center px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-all rounded-none border ${isDark ? 'border-emerald-500/50 bg-[#111] text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500' : 'border-emerald-600/50 bg-white text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600'}`}
					>
						<Plus className="w-3.5 h-3.5 mr-2" />
						<span>NEW_ENTRY</span>
					</button>
				</div>
			) : viewMode === 'table' ? (
				/* Passwords Table */
				<div className={`rounded-none overflow-hidden ${isDark ? 'bg-[#0a0a0a] border border-neutral-800' : 'bg-white border border-neutral-200'} shadow-[0_10px_30px_rgba(0,0,0,0.2)]`}>
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className={`${isDark ? 'bg-[#111]' : 'bg-neutral-50'}`}>
								<tr className={`border-b ${isDark ? 'border-neutral-800' : 'border-neutral-200'}`}>
									<th 
										onClick={() => handleSort('title')}
										className={`px-4 py-3 text-left text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em] cursor-pointer ${isDark ? 'hover:bg-emerald-500/5' : 'hover:bg-emerald-500/5'} transition-all`}
									>
										[ENTRY.TITLE] <SortIcon field="title" />
									</th>
									<th className={`px-4 py-3 text-left text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em] hidden md:table-cell`}>
										[USER.IDENTIFIER]
									</th>
									<th className={`px-4 py-3 text-left text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em] hidden lg:table-cell`}>
										[AUTH.CREDENTIAL]
									</th>
									<th 
										onClick={() => handleSort('website')}
										className={`px-4 py-3 text-left text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em] hidden md:table-cell cursor-pointer ${isDark ? 'hover:bg-emerald-500/5' : 'hover:bg-emerald-500/5'} transition-all`}
									>
										[URL.RESOURCE] <SortIcon field="website" />
									</th>
									<th 
										onClick={() => handleSort('is_active')}
										className={`px-4 py-3 text-left text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em] hidden lg:table-cell cursor-pointer ${isDark ? 'hover:bg-emerald-500/5' : 'hover:bg-emerald-500/5'} transition-all`}
									>
										[SYS.STATUS] <SortIcon field="is_active" />
									</th>
									<th className={`px-4 py-3 text-right text-[10px] font-mono font-bold ${isDark ? 'text-emerald-500/70' : 'text-emerald-700/70'} uppercase tracking-[0.2em]`}>
										[OP.CMD]
									</th>
								</tr>
							</thead>
							<tbody className={`divide-y ${isDark ? 'divide-neutral-900' : 'divide-neutral-100'}`}>
								{sortedPasswords.map((password) => (
								<tr key={password.id} className={isDark ? 'hover:bg-emerald-500/[0.02] transition-colors' : 'hover:bg-emerald-500/[0.02] transition-colors'}>
									<td className="px-4 py-4">
										<div className="flex flex-col">
											<h3 className={`text-xs font-mono font-bold uppercase tracking-wider ${isDark ? 'text-emerald-400' : 'text-emerald-800'}`}>
												{password.title}
											</h3>
											{password.description && (
												<p className={`text-[10px] font-mono uppercase mt-1 opacity-60 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>
													{password.description}
												</p>
											)}
											{password.tag_names && (
												<div className="flex flex-wrap gap-1 mt-2">
													{password.tag_names.split(',').map((tagName, index) => {
														const tag = tags.find(t => t.name === tagName);
														return (
															<span 
																key={index} 
																className="text-[9px] font-mono px-1.5 py-0.5 border uppercase tracking-widest" 
																style={{ 
																	borderColor: `${tag?.color || '#10b981'}44`, 
																	color: tag?.color || '#10b981',
																	backgroundColor: `${tag?.color || '#10b981'}11`
																}}
															>
																{tagName}
															</span>
														);
													})}
												</div>
											)}
										</div>
									</td>
									<td className="px-4 py-4 hidden md:table-cell">
										<div className="flex flex-col space-y-1">
											{password.username && (
												<div className="flex items-center space-x-2">
													<span className={`text-[10px] font-mono truncate max-w-xs ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
														{password.username}
													</span>
													<button onClick={() => copyToClipboard(password.username!)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
														<Copy className="w-3 h-3" />
													</button>
												</div>
											)}
											{password.email && (
												<div className="flex items-center space-x-2">
													<span className={`text-[10px] font-mono truncate max-w-xs ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
														{password.email}
													</span>
													<button onClick={() => copyToClipboard(password.email!)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
														<Copy className="w-3 h-3" />
													</button>
												</div>
											)}
										</div>
									</td>
									<td className="px-4 py-4 hidden lg:table-cell">
										<div className="flex items-center space-x-2">
											<span className={`text-[10px] font-mono tracking-widest truncate max-w-xs ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
												{showPasswords[password.id] ? password.password : '••••••••'}
											</span>
											<button onClick={() => togglePasswordVisibility(password.id)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
												{showPasswords[password.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
											</button>
											<button onClick={() => copyToClipboard(password.password)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
												<Copy className="w-3.5 h-3.5" />
											</button>
										</div>
									</td>
									<td className="px-4 py-4 hidden md:table-cell">
										{password.website ? (
											<div className="flex items-center space-x-2">
												<span className={`text-[10px] font-mono truncate max-w-xs ${isDark ? 'text-emerald-500/60' : 'text-emerald-700/60'}`}>
													{password.website}
												</span>
												<a
													href={password.website.startsWith('http') ? password.website : `https://${password.website}`}
													target="_blank"
													rel="noopener noreferrer"
													className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}
												>
													<Globe className="w-3.5 h-3.5" />
												</a>
											</div>
										) : (
											<span className={`text-[10px] font-mono ${isDark ? 'text-neutral-700' : 'text-neutral-300'}`}>[NONE]</span>
										)}
									</td>
									<td className="px-4 py-4 hidden lg:table-cell">
										<span className={`text-[9px] font-mono px-2 py-0.5 border uppercase tracking-widest ${password.is_active ? (isDark ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-emerald-600/30 text-emerald-700 bg-emerald-50') : (isDark ? 'border-neutral-800 text-neutral-600' : 'border-neutral-200 text-neutral-400')}`}>
											{password.is_active ? 'STATE.ACTIVE' : 'STATE.IDLE'}
										</span>
									</td>
									<td className="px-4 py-4">
										<div className="flex items-center justify-end space-x-3">
											<button onClick={() => openEditModal(password)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
												<Edit className="w-3.5 h-3.5" />
											</button>
											<button onClick={() => handleDelete(password.id)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-red-500/80' : 'text-neutral-400 hover:text-red-600'}`}>
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										</div>
									</td>
								</tr>
							))}
							</tbody>
						</table>
					</div>
				</div>
			) : (
				/* Passwords Grid */
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{sortedPasswords.map((password) => (
						<div key={password.id} className={`p-5 rounded-none border transition-all ${isDark ? 'bg-[#0a0a0a] border-neutral-800 hover:border-emerald-500/50' : 'bg-white border-neutral-200 hover:border-emerald-600/50'} shadow-sm hover:shadow-md relative overflow-hidden group`}>
							{/* FUI Corner detail */}
							<div className={`absolute top-0 right-0 w-8 h-8 border-t border-r transition-colors ${isDark ? 'border-neutral-800 group-hover:border-emerald-500/30' : 'border-neutral-200 group-hover:border-emerald-600/30'}`} />
							
							<div className="flex items-start justify-between mb-4 relative z-10">
								<div className="flex-1">
									<h3 className={`text-xs font-mono font-bold mb-1 uppercase tracking-wider ${isDark ? 'text-emerald-400' : 'text-emerald-800'}`}>
										{password.title}
									</h3>
									{password.description && (
										<p className={`text-[10px] font-mono uppercase opacity-60 line-clamp-2 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
											{password.description}
										</p>
									)}
								</div>
								<div className="flex items-center space-x-1 ml-2">
									<button onClick={() => openEditModal(password)} className={`p-1.5 transition-colors border ${isDark ? 'border-neutral-800 hover:border-emerald-500/50 text-neutral-600 hover:text-emerald-400' : 'border-neutral-100 hover:border-emerald-600/50 text-neutral-400 hover:text-emerald-600'}`}>
										<Edit className="w-3.5 h-3.5" />
									</button>
									<button onClick={() => handleDelete(password.id)} className={`p-1.5 transition-colors border ${isDark ? 'border-neutral-800 hover:border-red-500/30 text-neutral-600 hover:text-red-500' : 'border-neutral-100 hover:border-red-600/30 text-neutral-400 hover:text-red-600'}`}>
										<Trash2 className="w-3.5 h-3.5" />
									</button>
								</div>
							</div>
							
							<div className="space-y-4 relative z-10">
								{(password.username || password.email) && (
									<div className="border-t border-neutral-800/20 pt-3">
										<label className={`text-[9px] font-mono font-bold uppercase tracking-widest block mb-1.5 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>
											[USER_UID]
										</label>
										<div className="flex items-center space-x-2">
											<span className={`text-[10px] font-mono truncate flex-1 ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
												{password.username || password.email}
											</span>
											<button onClick={() => copyToClipboard(password.username || password.email || '')} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
												<Copy className="w-3.5 h-3.5" />
											</button>
										</div>
									</div>
								)}
								
								<div className="border-t border-neutral-800/20 pt-3">
									<label className={`text-[9px] font-mono font-bold uppercase tracking-widest block mb-1.5 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[AUTH_KEY]</label>
									<div className="flex items-center space-x-2">
										<span className={`text-[10px] font-mono tracking-[0.2em] truncate flex-1 ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>
											{showPasswords[password.id] ? password.password : '••••••••'}
										</span>
										<button onClick={() => togglePasswordVisibility(password.id)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
											{showPasswords[password.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
										</button>
										<button onClick={() => copyToClipboard(password.password)} className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}>
											<Copy className="w-3.5 h-3.5" />
										</button>
									</div>
								</div>

								{password.website && (
									<div className="border-t border-neutral-800/20 pt-3">
										<label className={`text-[9px] font-mono font-bold uppercase tracking-widest block mb-1.5 ${isDark ? 'text-neutral-600' : 'text-neutral-400'}`}>[NET_TARGET]</label>
										<div className="flex items-center space-x-2">
											<span className={`text-[10px] font-mono truncate flex-1 ${isDark ? 'text-emerald-500/60' : 'text-emerald-700/60'}`}>
												{password.website}
											</span>
											<a
												href={password.website.startsWith('http') ? password.website : `https://${password.website}`}
												target="_blank"
												rel="noopener noreferrer"
												className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}
											>
												<Globe className="w-3.5 h-3.5" />
											</a>
										</div>
									</div>
								)}

								<div className="flex items-center justify-between pt-4 border-t border-neutral-800/20">
									<span className={`text-[9px] font-mono px-2 py-0.5 border uppercase tracking-widest ${password.is_active ? (isDark ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-emerald-600/30 text-emerald-700 bg-emerald-50') : (isDark ? 'border-neutral-800 text-neutral-600' : 'border-neutral-200 text-neutral-400')}`}>
										{password.is_active ? 'ACTIVE' : 'IDLE'}
									</span>
									<span className={`text-[8px] font-mono uppercase ${isDark ? 'text-neutral-700' : 'text-neutral-400'}`}>
										SEC_ID: {password.id.toString().padStart(4, '0')}
									</span>
								</div>

								{password.tag_names && (
									<div className="flex flex-wrap gap-1 pt-2">
										{password.tag_names.split(',').map((tagName, index) => {
											const tag = tags.find(t => t.name === tagName);
											return (
												<span 
													key={index} 
													className="text-[8px] font-mono px-1.5 py-0.5 border uppercase tracking-[0.1em]" 
													style={{ 
														borderColor: `${tag?.color || '#10b981'}33`, 
														color: tag?.color || '#10b981',
														backgroundColor: `${tag?.color || '#10b981'}08`
													}}
												>
													{tagName}
												</span>
											);
										})}
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			)}



			{showModal && createPortal(
				<div
					className="fixed inset-0 bg-[#000000cc] backdrop-blur-sm flex items-center justify-center z-[1005] p-4 overflow-y-auto font-mono"
					onClick={() => !submitting && closeModal()}
				>
					<div
						className={`relative w-full max-w-lg border rounded-none p-8 overflow-hidden transition-all shadow-[0_0_50px_rgba(0,0,0,0.5)] ${isDark ? 'bg-[#0a0a0a] border-emerald-500/30' : 'bg-white border-emerald-600/30'}`}
						onClick={(e) => e.stopPropagation()}
					>
						{/* FUI Scanning Line */}
						<div className="absolute top-0 left-0 w-full h-[2px] bg-emerald-500/30 shadow-[0_0_15px_#10b981] opacity-20 animate-[scan-move_4s_ease-in-out_infinite]" />
						
						{/* FUI Corner accents */}
						<div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-emerald-500/50" />
						<div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-emerald-500/50" />

						<div className="flex justify-between items-center mb-8">
							<div>
								<h2 className={`text-sm font-bold uppercase tracking-[0.3em] ${isDark ? 'text-emerald-400' : 'text-emerald-800'}`}>
									{editingPassword ? '[UPDATE.BUFFER]' : '[INITIALIZE.ENTRY]'}
								</h2>
								<div className="mt-1 h-[1px] w-12 bg-emerald-500/50" />
							</div>
							<button 
								onClick={closeModal} 
								className={`p-1 transition-colors ${isDark ? 'text-neutral-600 hover:text-emerald-400' : 'text-neutral-400 hover:text-emerald-600'}`}
							>
								<X className="w-5 h-5" />
							</button>
						</div>

						<form onSubmit={editingPassword ? handleEditSubmit : handleSubmit} className="space-y-6">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div className="md:col-span-2">
									<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										ENTRY_TITLE <span className="text-emerald-500">*</span>
									</label>
									<input
										type="text"
										required
										value={formData.title}
										onChange={(e) => setFormData({ ...formData, title: e.target.value })}
										className={`w-full px-4 py-3 rounded-none border text-xs font-mono transition-all focus:outline-none ${isDark ? 'bg-[#111] border-neutral-800 text-emerald-50 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-200 text-emerald-900 focus:border-emerald-600/50'}`}
										placeholder="e.g. AWS_ADMIN_VAULT"
									/>
								</div>
								
								<div>
									<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										IDENTIFIER
									</label>
									<input
										type="text"
										value={formData.username}
										onChange={(e) => setFormData({ ...formData, username: e.target.value })}
										className={`w-full px-4 py-3 rounded-none border text-xs font-mono transition-all focus:outline-none ${isDark ? 'bg-[#111] border-neutral-800 text-emerald-50 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-200 text-emerald-900 focus:border-emerald-600/50'}`}
										placeholder="USERNAME"
									/>
								</div>

								<div>
									<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										SECURE_EMAIL
									</label>
									<input
										type="text"
										value={formData.email}
										onChange={(e) => setFormData({ ...formData, email: e.target.value })}
										className={`w-full px-4 py-3 rounded-none border text-xs font-mono transition-all focus:outline-none ${isDark ? 'bg-[#111] border-neutral-800 text-emerald-50 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-200 text-emerald-900 focus:border-emerald-600/50'}`}
										placeholder="EMAIL_ADDR"
									/>
								</div>

								<div className="md:col-span-2">
									<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										ACCESS_KEY <span className="text-emerald-500">*</span>
									</label>
									<div className="flex space-x-2">
										<input
											type="text"
											required
											value={formData.password}
											onChange={(e) => setFormData({ ...formData, password: e.target.value })}
											className={`flex-1 px-4 py-3 rounded-none border text-xs font-mono transition-all focus:outline-none tracking-widest ${isDark ? 'bg-[#111] border-neutral-800 text-emerald-50 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-200 text-emerald-900 focus:border-emerald-600/50'}`}
											placeholder="P@SSWD_SEC"
										/>
										<button
											type="button"
											onClick={handleGeneratePassword}
											className={`px-4 py-2 border font-bold text-[10px] uppercase tracking-widest transition-all ${isDark ? 'border-neutral-800 bg-neutral-900/50 text-emerald-500 hover:bg-emerald-500/10 hover:border-emerald-500' : 'border-neutral-200 bg-neutral-50 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600'}`}
										>
											GEN_RAND
										</button>
									</div>
								</div>

								<div className="md:col-span-2">
									<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										RESOURCE_URL
									</label>
									<input
										type="text"
										value={formData.website}
										onChange={(e) => setFormData({ ...formData, website: e.target.value })}
										className={`w-full px-4 py-3 rounded-none border text-xs font-mono transition-all focus:outline-none ${isDark ? 'bg-[#111] border-neutral-800 text-emerald-50 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-200 text-emerald-900 focus:border-emerald-600/50'}`}
										placeholder="https://vault.target.com"
									/>
								</div>

								<div className="md:col-span-2">
									<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-2 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										METADATA_DESCRIPTION
									</label>
									<textarea
										value={formData.description}
										onChange={(e) => setFormData({ ...formData, description: e.target.value })}
										className={`w-full px-4 py-3 rounded-none border text-xs font-mono transition-all focus:outline-none min-h-[100px] resize-none ${isDark ? 'bg-[#111] border-neutral-800 text-emerald-50 focus:border-emerald-500/50' : 'bg-neutral-50 border-neutral-200 text-emerald-900 focus:border-emerald-600/50'}`}
										placeholder="ADDITIONAL_DATA..."
									/>
								</div>

								<div className="md:col-span-2">
									<label className={`block text-[10px] font-bold uppercase tracking-[0.2em] mb-3 ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
										ASSIGN_TAGS
									</label>
									<div className="flex flex-wrap gap-3 p-4 border border-neutral-800/20 bg-neutral-900/10">
										{tags.map(tag => (
											<label key={tag.id} className="flex items-center cursor-pointer group">
												<div className="relative flex items-center">
													<input
														type="checkbox"
														checked={formData.tag_ids.includes(tag.id)}
														onChange={(e) => {
															if (e.target.checked) {
																setFormData({ ...formData, tag_ids: [...formData.tag_ids, tag.id] });
															} else {
																setFormData({ ...formData, tag_ids: formData.tag_ids.filter(id => id !== tag.id) });
															}
														}}
														className="sr-only"
													/>
													<div className={`w-4 h-4 border transition-all flex items-center justify-center ${formData.tag_ids.includes(tag.id) ? 'border-emerald-500 bg-emerald-500/20' : 'border-neutral-700 bg-transparent group-hover:border-emerald-500/50'}`}>
														{formData.tag_ids.includes(tag.id) && <div className="w-1.5 h-1.5 bg-emerald-500" />}
													</div>
												</div>
												<span className={`ml-2 text-[10px] font-mono font-bold uppercase tracking-widest ${formData.tag_ids.includes(tag.id) ? 'text-emerald-400' : 'text-neutral-500'}`}>
													{tag.name}
												</span>
											</label>
										))}
									</div>
								</div>
							</div>

							<div className="flex justify-end space-x-4 pt-4">
								<button
									type="button"
									onClick={closeModal}
									disabled={submitting}
									className={`px-6 py-2 border font-mono text-[10px] uppercase tracking-widest transition-all ${isDark ? 'border-neutral-800 bg-neutral-900/30 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300' : 'border-neutral-200 bg-neutral-50 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600'} ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
								>
									[CANCEL]
								</button>
								<button
									type="submit"
									disabled={submitting}
									className={`px-8 py-2 border font-mono font-bold text-[10px] uppercase tracking-[0.2em] transition-all flex items-center space-x-3 ${isDark ? 'border-emerald-500/50 bg-[#111] text-emerald-400 hover:bg-emerald-500/20 hover:border-emerald-500' : 'border-emerald-600/50 bg-white text-emerald-800 hover:bg-emerald-50 hover:border-emerald-600'} disabled:opacity-50 disabled:cursor-not-allowed`}
								>
									{submitting && (
										<div className="w-3 h-3 border border-emerald-500 border-t-transparent animate-spin" />
									)}
									<span>{editingPassword ? (submitting ? 'EXECUTING...' : '[COMMIT_CHANGES]') : (submitting ? 'EXECUTING...' : '[COMMIT_ENTRY]')}</span>
								</button>
							</div>
						</form>
					</div>
				</div>,
				document.body
			)}
		</div>
	);
};

export default Passwords;

